import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose
} from '../ui/Dialog'
import { Button } from '../ui/Button'
import { 
  AlertTriangle,
  Loader2,
  Trash2,
  X,
  Building2
} from 'lucide-react'
import authService from '../../services/authService'

const DeleteHospitalModal = ({ hospital, open, onOpenChange, onSuccess }) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState(null)

  if (!hospital) return null

  // Extract hospital information
  const hospitalName = hospital.name || 'Unknown Hospital'
  const hospitalId = hospital.id || hospital.hospital_id
  const hospitalLocation = hospital.location || hospital.address || 'N/A'
  const hospitalStatus = hospital.status || 'active'
  const hospitalUsers = hospital.users || 0
  const hospitalRequests = hospital.requests || 0

  const handleDelete = async () => {
    if (!hospitalId) {
      setError('Hospital ID not found')
      return
    }

    setIsDeleting(true)
    setError(null)

    try {
      // Call API to delete hospital
      const response = await authService.deleteHospital(hospitalId)

      if (response.success !== false) {
        // Call onSuccess callback to refresh the hospital list
        if (onSuccess) {
          onSuccess()
        }
        onOpenChange(false)
      } else {
        throw new Error(response.message || 'Failed to delete hospital')
      }
    } catch (err) {
      console.error('Error deleting hospital:', err)
      setError(err.response?.data?.message || err.message || 'Failed to delete hospital. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleClose = () => {
    if (!isDeleting) {
      setError(null)
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0">
        <DialogClose className="absolute right-4 top-4 z-10" disabled={isDeleting} />
        
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Delete Hospital
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm text-gray-500">
                This action cannot be undone
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Confirmation Message */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-700 leading-relaxed">
              Are you sure you want to delete <span className="font-semibold text-gray-900">{hospitalName}</span>?
            </p>
            <p className="text-xs text-gray-500 mt-2">
              This will permanently remove the hospital from the system. All associated data will be deleted.
            </p>
          </div>

          {/* Hospital Details */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Hospital Information</h4>
            <div className="space-y-2.5">
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-500 font-medium">Hospital Name:</span>
                <span className="text-sm font-semibold text-gray-900 text-right max-w-[60%] break-words">{hospitalName}</span>
              </div>
              
              {hospitalLocation && hospitalLocation !== 'N/A' && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-500 font-medium">Location:</span>
                  <span className="text-sm font-medium text-gray-900 text-right max-w-[60%] break-words">{hospitalLocation}</span>
                </div>
              )}
              
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-500 font-medium">Status:</span>
                <span className="text-sm font-medium text-gray-900 text-right max-w-[60%] capitalize">{hospitalStatus}</span>
              </div>
              
              {hospitalUsers !== undefined && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-500 font-medium">Total Users:</span>
                  <span className="text-sm font-medium text-gray-900 text-right max-w-[60%] font-mono">{hospitalUsers}</span>
                </div>
              )}
              
              {hospitalRequests !== undefined && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-500 font-medium">Total Requests:</span>
                  <span className="text-sm font-medium text-gray-900 text-right max-w-[60%] font-mono">{hospitalRequests}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-gray-200">
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Hospital
                </>
              )}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleClose}
              disabled={isDeleting}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default DeleteHospitalModal

